import { Route as DefaultAboutRoute } from '@trashlab/sitesapp/src/routes/default/about';
import { Route as DefaultCartCartIdRoute } from '@trashlab/sitesapp/src/routes/default/cart/$cartId';
import { Route as DefaultCartCartIdThankyouRoute } from '@trashlab/sitesapp/src/routes/default/cart/$cartId/thankyou';
import { Route as DefaultContactUsRoute } from '@trashlab/sitesapp/src/routes/default/contact-us';
import { Route as DefaultDumpsterFaqRoute } from '@trashlab/sitesapp/src/routes/default/dumpster-faq';
import { Route as DefaultDumpsterRentalsRoute } from '@trashlab/sitesapp/src/routes/default/dumpster-rentals';
import { Route as DefaultDumpsterRentalsDumpsterIdRoute } from '@trashlab/sitesapp/src/routes/default/dumpster-rentals/$dumpsterId';
import { Route as DefaultHomeRoute } from '@trashlab/sitesapp/src/routes/default/home';
import { Route as DefaultPrivacyPolicyRoute } from '@trashlab/sitesapp/src/routes/default/privacy-policy';
import { Route as DefaultServiceAreasRoute } from '@trashlab/sitesapp/src/routes/default/service-areas';
import { Route as DefaultTermsAndConditionsRoute } from '@trashlab/sitesapp/src/routes/default/terms-and-conditions';
import { Route as RedebinsAboutUsRoute } from '@trashlab/sitesapp/src/routes/redebins/about-us';
import { Route as RedebinsCartCartIdRoute } from '@trashlab/sitesapp/src/routes/redebins/cart/$cartId';
import { Route as RedebinsCartCartIdThankyouRoute } from '@trashlab/sitesapp/src/routes/redebins/cart/$cartId/thankyou';
import { Route as RedebinsCommercialRoute } from '@trashlab/sitesapp/src/routes/redebins/commercial';
import { Route as RedebinsContactRoute } from '@trashlab/sitesapp/src/routes/redebins/contact';
import { Route as RedebinsDebrisCalculatorRoute } from '@trashlab/sitesapp/src/routes/redebins/debris-calculator';
import { Route as RedebinsDumpsterRentalGuideRoute } from '@trashlab/sitesapp/src/routes/redebins/dumpster-rental-guide';
import { Route as RedebinsDumpsterRentalsRoute } from '@trashlab/sitesapp/src/routes/redebins/dumpster-rentals';
import { Route as RedebinsDumpsterSizesRoute } from '@trashlab/sitesapp/src/routes/redebins/dumpster-sizes';
import { Route as RedebinsFaqRoute } from '@trashlab/sitesapp/src/routes/redebins/faq';
import { Route as RedebinsHomeRoute } from '@trashlab/sitesapp/src/routes/redebins/home';
import { Route as RedebinsHowWeMeasureUpRoute } from '@trashlab/sitesapp/src/routes/redebins/how-we-measure-up';
import { Route as RedebinsJunkRemovalRoute } from '@trashlab/sitesapp/src/routes/redebins/junk-removal';
import { Route as RedebinsProhibitedItemsRoute } from '@trashlab/sitesapp/src/routes/redebins/prohibited-items';
import { Route as RedebinsResidentialRoute } from '@trashlab/sitesapp/src/routes/redebins/residential';
import { Route as RedebinsServiceAreasRoute } from '@trashlab/sitesapp/src/routes/redebins/service-areas';
import { Route as RedebinsServiceAreasCityRoute } from '@trashlab/sitesapp/src/routes/redebins/service-areas/$city';
import { Route as RedebinsServiceAreasCountyCityRoute } from '@trashlab/sitesapp/src/routes/redebins/service-areas/$county/$city';
import { Route as RedebinsThankYouRoute } from '@trashlab/sitesapp/src/routes/redebins/thank-you';
import { Route as SecondaryAboutRoute } from '@trashlab/sitesapp/src/routes/secondary/about';
import { Route as SecondaryContactUsRoute } from '@trashlab/sitesapp/src/routes/secondary/contact-us';
import { Route as SecondaryHomeRoute } from '@trashlab/sitesapp/src/routes/secondary/home';

import {
  createRoute,
  type ParseParamsFn,
  type ResolveParams,
} from '@tanstack/react-router';
import { rootRoute } from '@trashlab/sitesapp/src/routes/__root';
import { useTenantContext } from '@trashlab/sitesapp/src/contexts/use-tenant-context';
import { ComponentType } from 'react';
import { NotFoundComponent } from '@trashlab/sitesapp/src/components/not-found';
// @preserve-path
import { WebsiteTemplateSchema } from '@trashlab/api/src/domains/website/schemas/organization-website';

type TemplateRouteConfig<TPath extends string> = {
  [K in WebsiteTemplateSchema]?: {
    component: ComponentType | undefined;
    validateSearch?: NonNullable<
      typeof DefaultDumpsterRentalsRoute.options.validateSearch
    >;
    loader?: NonNullable<typeof DefaultDumpsterRentalsRoute.options.loader>;
    params?: {
      parse?: ParseParamsFn<TPath, ResolveParams<TPath>>;
    };
  };
};

const createTemplateAwareComponent = <TPath extends string>(
  routes: TemplateRouteConfig<TPath>,
) => {
  return () => {
    const { website } = useTenantContext();
    const template = website?.template ?? 'DEFAULT';
    const selectedRoute = routes[template];

    if (!website || !selectedRoute || !selectedRoute.component) {
      return <NotFoundComponent />;
    }

    const Component = selectedRoute.component;
    return <Component />;
  };
};

const createTemplateAwareRoute = <const TPath extends string>(
  path: TPath,
  routes: TemplateRouteConfig<TPath>,
) => {
  const routeConfig = {
    getParentRoute: () => rootRoute,
    path,
    component: createTemplateAwareComponent(routes),
  };

  const routeWithOptions =
    routes.DEFAULT ?? routes.REDEBINS ?? routes.SECONDARY;
  if (!routeWithOptions) return createRoute(routeConfig);

  const routeOptions = {
    validateSearch: routeWithOptions.validateSearch,
    loader: routeWithOptions.loader,
    params: routeWithOptions.params,
  };

  return createRoute({
    ...routeConfig,
    ...Object.fromEntries(
      Object.entries(routeOptions).filter(([_, value]) => value !== undefined),
    ),
  });
};

const routes = [
createTemplateAwareRoute('/about', {
  DEFAULT: {
    component: DefaultAboutRoute.options.component
  },
  SECONDARY: {
    component: SecondaryAboutRoute.options.component
  }
}),
createTemplateAwareRoute('/about-us', {
  REDEBINS: {
    component: RedebinsAboutUsRoute.options.component
  }
}),
createTemplateAwareRoute('/cart/$cartId', {
  DEFAULT: {
    component: DefaultCartCartIdRoute.options.component,
    params: {
      parse: DefaultCartCartIdRoute.options.params?.parse
    }
  },
  REDEBINS: {
    component: RedebinsCartCartIdRoute.options.component,
    params: {
      parse: RedebinsCartCartIdRoute.options.params?.parse
    }
  }
}),
createTemplateAwareRoute('/cart/$cartId/thankyou', {
  DEFAULT: {
    component: DefaultCartCartIdThankyouRoute.options.component,
    params: {
      parse: DefaultCartCartIdThankyouRoute.options.params?.parse
    }
  },
  REDEBINS: {
    component: RedebinsCartCartIdThankyouRoute.options.component,
    params: {
      parse: RedebinsCartCartIdThankyouRoute.options.params?.parse
    }
  }
}),
createTemplateAwareRoute('/commercial', {
  REDEBINS: {
    component: RedebinsCommercialRoute.options.component
  }
}),
createTemplateAwareRoute('/contact', {
  REDEBINS: {
    component: RedebinsContactRoute.options.component
  }
}),
createTemplateAwareRoute('/contact-us', {
  DEFAULT: {
    component: DefaultContactUsRoute.options.component
  },
  SECONDARY: {
    component: SecondaryContactUsRoute.options.component
  }
}),
createTemplateAwareRoute('/debris-calculator', {
  REDEBINS: {
    component: RedebinsDebrisCalculatorRoute.options.component
  }
}),
createTemplateAwareRoute('/dumpster-faq', {
  DEFAULT: {
    component: DefaultDumpsterFaqRoute.options.component
  }
}),
createTemplateAwareRoute('/dumpster-rental-guide', {
  REDEBINS: {
    component: RedebinsDumpsterRentalGuideRoute.options.component
  }
}),
createTemplateAwareRoute('/dumpster-rentals', {
  DEFAULT: {
    component: DefaultDumpsterRentalsRoute.options.component,
    validateSearch: DefaultDumpsterRentalsRoute.options.validateSearch,
    loader: DefaultDumpsterRentalsRoute.options.loader
  },
  REDEBINS: {
    component: RedebinsDumpsterRentalsRoute.options.component,
    validateSearch: RedebinsDumpsterRentalsRoute.options.validateSearch,
    loader: RedebinsDumpsterRentalsRoute.options.loader
  }
}),
createTemplateAwareRoute('/dumpster-rentals/$dumpsterId', {
  DEFAULT: {
    component: DefaultDumpsterRentalsDumpsterIdRoute.options.component,
    params: {
      parse: DefaultDumpsterRentalsDumpsterIdRoute.options.params?.parse
    }
  }
}),
createTemplateAwareRoute('/dumpster-sizes', {
  REDEBINS: {
    component: RedebinsDumpsterSizesRoute.options.component
  }
}),
createTemplateAwareRoute('/faq', {
  REDEBINS: {
    component: RedebinsFaqRoute.options.component
  }
}),
createTemplateAwareRoute('/', {
  DEFAULT: {
    component: DefaultHomeRoute.options.component
  },
  REDEBINS: {
    component: RedebinsHomeRoute.options.component
  },
  SECONDARY: {
    component: SecondaryHomeRoute.options.component
  }
}),
createTemplateAwareRoute('/how-we-measure-up', {
  REDEBINS: {
    component: RedebinsHowWeMeasureUpRoute.options.component
  }
}),
createTemplateAwareRoute('/junk-removal', {
  REDEBINS: {
    component: RedebinsJunkRemovalRoute.options.component
  }
}),
createTemplateAwareRoute('/order-received', {
  REDEBINS: {
    component: RedebinsThankYouRoute.options.component
  }
}),
createTemplateAwareRoute('/privacy-policy', {
  DEFAULT: {
    component: DefaultPrivacyPolicyRoute.options.component
  }
}),
createTemplateAwareRoute('/prohibited-items', {
  REDEBINS: {
    component: RedebinsProhibitedItemsRoute.options.component
  }
}),
createTemplateAwareRoute('/residential', {
  REDEBINS: {
    component: RedebinsResidentialRoute.options.component
  }
}),
createTemplateAwareRoute('/service-areas', {
  DEFAULT: {
    component: DefaultServiceAreasRoute.options.component
  },
  REDEBINS: {
    component: RedebinsServiceAreasRoute.options.component
  }
}),
createTemplateAwareRoute('/service-areas/$city', {
  REDEBINS: {
    component: RedebinsServiceAreasCityRoute.options.component,
    params: {
      parse: RedebinsServiceAreasCityRoute.options.params?.parse
    }
  }
}),
createTemplateAwareRoute('/service-areas/$county/$city', {
  REDEBINS: {
    component: RedebinsServiceAreasCountyCityRoute.options.component,
    params: {
      parse: RedebinsServiceAreasCountyCityRoute.options.params?.parse
    }
  }
}),
createTemplateAwareRoute('/terms-and-conditions', {
  DEFAULT: {
    component: DefaultTermsAndConditionsRoute.options.component
  }
}),
createTemplateAwareRoute('/thank-you', {
  REDEBINS: {
    component: RedebinsThankYouRoute.options.component
  }
})
];

rootRoute.addChildren(routes);
